import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { FiZap, FiEdit3, FiUsers, FiStar } from 'react-icons/fi';
import { motion, useInView } from 'framer-motion';
import './BenefitSection.css';
import { isIOS } from '../../hooks/isIos';

const Zap = FiZap as React.FC<React.SVGProps<SVGSVGElement>>;
const Edit3 = FiEdit3 as React.FC<React.SVGProps<SVGSVGElement>>;
const Family = FiUsers as React.FC<React.SVGProps<SVGSVGElement>>;
const Star = FiStar as React.FC<React.SVGProps<SVGSVGElement>>;

export function BenefitSection() {
	const ref = useRef(null);
	const marginValue = isIOS() ? '0px' : '-350px';
	const isInView = useInView(ref, { once: true, margin: marginValue });

	const benefits = [
		{
			icon: Zap,
			title: 'Frühzeitiger Zugriff',
			description:
				'Teste neue Funktionen noch vor dem offiziellen Release und erlebe die App im Aufbau.',
		},
		{
			icon: Edit3,
			title: 'Mitgestalten statt nur nutzen',
			description:
				'Dein Feedback beeinflusst die App direkt – du wirst Teil der Weiterentwicklung.',
		},
		{
			icon: Family,
			title: 'Gemeinsam für gerechteren Familienalltag',
			description:
				'Hilf mit, Aufgaben fair zu verteilen und den Alltag friedlicher zu gestalten.',
		},
		{
			icon: Star,
			title: 'Lebenslanger \nPremium-Zugang',
			description:
				'Als Dankeschön für deinen Beitrag erhältst du dauerhaft kostenlosen Zugriff auf alle Premium-Funktionen.',
			isPremium: true,
		},
	];

	return (
		<section className='benefits-section-alt' ref={ref}>
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={isInView ? { opacity: 1, y: 0 } : {}}
				transition={{ duration: 0.6 }}
			>
				<h1>
					Warum <span className='highlight'>Erledix Beta-Tester:in</span> werden?
				</h1>
			</motion.div>

			<ul className='benefit-list'>
				{benefits.map(({ icon: Icon, title, description, isPremium }, i) => (
					<motion.li
						key={i}
						className={isPremium ? 'premium-line' : undefined}
						initial={{ opacity: 0, y: 30 }}
						animate={isInView ? { opacity: 1, y: 0 } : {}}
						transition={{
							delay: 0.2 + i * 0.15,
							duration: 0.6,
							ease: 'easeOut',
						}}
					>
						<div className='benefit-icon'>
							<Icon />
						</div>
						<div className='benefit-text'>
							<h3 style={{ whiteSpace: isPremium ? 'pre-line' : 'normal' }}>
								{title}
							</h3>
							<p>{description}</p>
						</div>
					</motion.li>
				))}
			</ul>

			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={isInView ? { opacity: 1, y: 0 } : {}}
				transition={{ delay: 0.2 + benefits.length * 0.15, duration: 0.6 }}
				style={{ textAlign: 'center', marginTop: '2rem' }}
			>
				<Link to='/beta-signup' className='cta-button'>
					Werde Beta-Tester:in und gestalte mit uns die Zukunft!
				</Link>
			</motion.div>

			<motion.p
				className='benefit-footer'
				initial={{ opacity: 0, y: 10 }}
				animate={isInView ? { opacity: 1, y: 0 } : {}}
				transition={{ delay: 0.2 + (benefits.length + 1) * 0.15, duration: 0.6 }}
			>
				Gemeinsam gestalten wir eine App, die Familien wirklich hilft.
			</motion.p>
		</section>
	);
}
